import React, { useState, useEffect, useMemo, useCallback } from "react";
import makeStyles from '@mui/styles/makeStyles';
import { capitalize, Grid, Typography } from "@mui/material";
import EditableTable from "../../../../Controllers/EditableTable";
import service from "./../../helpers/GifService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLoading } from "../../../../providers/loadingProvider";
import { useNotify } from "../../../../providers/NotificationProvider";
import { useSelector } from "react-redux";
import { canEditSelector } from "../../../../store/applicantsSlice";
import { recommendedBy } from "../../Constants";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  selectTableCell: {
    minWidth: 150,
  },
  textError: {
    color: "red",
  },
  editButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.yellow.hover,
    },
    color: theme.palette.gif.yellow.dark,
    backgroundColor: theme.palette.gif.yellow.color,
  },
  saveButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  infoIcon: {
    marginLeft: 5,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const ValidateEmail = (mail) => {
  if (
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
      mail
    )
  ) {
    return true;
  }
  return false;
};

const buildProposalContact = (proposalRefereeContact, proposalReferee) => {
  proposalRefereeContact["titleCode"] = proposalReferee.titleCode;
  proposalRefereeContact["title"] = proposalReferee.title;
  proposalRefereeContact["firstName"] = proposalReferee.firstName;
  proposalRefereeContact["lastName"] = proposalReferee.lastName;
  proposalRefereeContact["institution"] = proposalReferee.institutionId;
  proposalRefereeContact["institutionName"] = proposalReferee.institutionName;
  proposalRefereeContact["websiteurl"] = proposalReferee.websiteurl;
  proposalRefereeContact["primaryEmail"] = proposalReferee.primaryEmail;
  proposalRefereeContact["country"] = proposalReferee.institutionCountryName;
  proposalRefereeContact["countryCode"] = proposalReferee.institutionCountryId;
};

export default function NoSendReviewers() {
  const classes = useStyles();
  const loading = useLoading();
  const notify = useNotify();
  const [proposalId, setproposalId] = useState();
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [titlesOptions, setTitlesOptions] = useState([]);
  const [proposalReferees, setProposalReferees] = useState([]);
  const canEdit = useSelector(canEditSelector);

  const validateRow = (row) => {
    let errorList = [];
    if (
      row.titleCode === null ||
      row.titleCode === undefined ||
      row.titleCode === ""
    )
      errorList.push("Title");
    if (
      row.firstName === null ||
      row.firstName === undefined ||
      row.firstName === ""
    )
      errorList.push("First Name");
    if (
      row.lastName === null ||
      row.lastName === undefined ||
      row.lastName === ""
    )
      errorList.push("Last Name");
    if (
      row.institutionName === null ||
      row.institutionName === undefined ||
      row.institutionName === ""
    )
      errorList.push("Institution");
    if (
      row.websiteurl === null ||
      row.websiteurl === undefined ||
      row.websiteurl === ""
    )
      errorList.push("WebSite");
    if (
      row.primaryEmail === null ||
      row.primaryEmail === undefined ||
      row.primaryEmail === ""
    )
      errorList.push("Email");

    if (errorList.length === 0) {
      if (ValidateEmail(row.primaryEmail)) return true;
      else {
        notify.show(`You have entered an invalid email address!`, {
          severity: "error",
        });
        return false;
      }
    }

    notify.show(`Missing Fields:  ${errorList.join(", ")}`, {
      severity: "error",
    });

    return false;
  };

  const setRow = async (row) => {
    const loadingId = loading.addLoading("Updating Reviewer");
    const capRow = {
      ...row,
      firstName: capitalize(row.firstName),
      lastName: capitalize(row.lastName),
    };
    var proposalRefereeContact = await service.GetContactByProposalRefereeId({
      ProposalRefereeId: capRow.id,
    });
    buildProposalContact(proposalRefereeContact, capRow);
    service
      .updateProposalRefreeByContact({
        proposalRefereeId: capRow.id,
        contact: proposalRefereeContact,
      })
      .then(() => {
        loading.removeLoading(loadingId);
        getProposalReferees(proposalId);
      });
  };

  const addRow = async (row) => {
    const loadingId = loading.addLoading("Creating Reviewer");
    var proposalRefereeContact = {};
    const capRow = {
      ...row,
      firstName: capitalize(row.firstName),
      lastName: capitalize(row.lastName),
    };
    buildProposalContact(proposalRefereeContact, capRow);
    service
      .createProposalRefreeByContact({
        proposalId: proposalId,
        contact: proposalRefereeContact,
        recommendedBy: recommendedBy.ApplicantReviewerNotToBeSent,
      })
      .then(() => {
        loading.removeLoading(loadingId);
        getProposalReferees(proposalId);
      });
  };

  const deleteRow = (proposalRefereeId) => {
    const loadingId = loading.addLoading("Removing Reviewer");
    service.DeactiveProposalRefferee({ proposalRefereeId }).then(() => {
      getProposalReferees(proposalId);
      loading.removeLoading(loadingId);
    });
  };

  const fillInstitutionsOptions = useCallback(async () => {
    const loadingId = loading.addLoading("Loading Institutions");
    var institutions = await service.getAllInstitutions(false);
    setInstitutionOptions(
      institutions.map((i) => ({ ...i, id: i.name, label: i.name }))
    );
    loading.removeLoading(loadingId);
  }, [loading]);

  const fillCountriesOptions = useCallback(async () => {
    const loadingId = loading.addLoading("Loading Countries");
    var countries = await service.getAllCountries();
    setCountriesOptions(
      countries.map((c) => ({ ...c, id: c.id, label: c.name }))
    );
    loading.removeLoading(loadingId);
  }, [loading]);

  const getProposalReferees = useCallback(
    async (proposalId) => {
      const loadingId = loading.addLoading("Loading Reviewers");
      var proposalReferees = await service.getProposalRefereesByProposalId({
        proposalId,
      });
      proposalReferees = proposalReferees.filter(
        (pr) => pr.recommendedBy === recommendedBy.ApplicantReviewerNotToBeSent
      );
      setProposalReferees(proposalReferees);
      loading.removeLoading(loadingId);
    },
    [loading]
  );

  const fillTitlesOptions = useCallback(async () => {
    const loadingId = loading.addLoading("Loading Titles");
    var titlesOptions = await service.GetContactTitles();
    setTitlesOptions(titlesOptions.map((t) => ({ ...t, id: t.value })));
    loading.removeLoading(loadingId);
  }, [loading]);

  const getProposalId = useCallback(async () => {
    const loadingId = loading.addLoading("Loading Applicant");
    const applicant = await service.getApplicant();
    loading.removeLoading(loadingId);
    return applicant.proposalfullnumber;
  }, [loading]);

  useEffect(() => {
    const getData = async () => {
      const proposalId = await getProposalId();
      setproposalId(proposalId);
      getProposalReferees(proposalId);
      fillInstitutionsOptions();
      fillCountriesOptions();
      fillTitlesOptions();
    };
    getData();
  }, [
    fillCountriesOptions,
    fillInstitutionsOptions,
    fillTitlesOptions,
    getProposalId,
    getProposalReferees,
    loading,
  ]);

  const tableConfig = useMemo(
    () => ({
      idField: "id",
      disableEdit: !canEdit,
      columns: [
        {
          id: "titleCode",
          label: "Title",
          field: "titleCode",
          type: "options",
          options: titlesOptions,
        },
        {
          id: "firstName",
          label: "First Name",
          field: "firstName",
          capitalize: true,
        },
        {
          id: "lastName",
          label: "Last Name",
          field: "lastName",
          capitalize: true,
        },
        {
          id: "institutionName",
          label: "Institution",
          field: "institutionName",
          type: "options",
          options: institutionOptions,
          width: 3,
        },
        {
          id: "websiteurl",
          label: "WebSite",
          field: "websiteurl",
          width: 2,
        },
        {
          id: "primaryEmail",
          label: "Email",
          field: "primaryEmail",
          width: 3,
        },
        {
          id: "institutionCountryId",
          label: "Country",
          field: "institutionCountryId",
          type: "options",
          options: countriesOptions,
          width: 2,
        },
      ],
    }),
    [institutionOptions, countriesOptions, titlesOptions, canEdit]
  );

  return (
    <>
      <Grid container>
        <Typography variant="subtitle2">Reviewers (Not to send)</Typography>
        <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
      </Grid>
      <EditableTable
        tableConfig={tableConfig}
        tableData={proposalReferees}
        rowValidate={validateRow}
        rowCreated={addRow}
        rowUpdated={setRow}
        rowDeleted={deleteRow}
      />
    </>
  );
}
